import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import { ChartData, ChartOptions } from 'chart.js';
import { forwardRef, useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { BasicChartProps } from '../DashboardWidget';
import ChartLegend from './ChartLegend/ChartLegend';
import { chartMaxHeight, defaultChartAnimation } from './chartConfig';
import { NonMatrixedChartData, buildChartColors, legendMouseLeave, legendMouseOver } from './chartUtils';

interface PieChartProps extends BasicChartProps {
  data: PieChartData;
  disableLegend?: boolean;
}

// CHARTDATA NEEDED to render a chart
export interface PieChartData extends NonMatrixedChartData {}

const PieChart = ({ title, data, colors: colorMap, loading, disableLegend = false, animation = defaultChartAnimation }: PieChartProps, ref: any) => {
  const pieRef = ref ? ref : useRef();
  const chartColors = buildChartColors(data.values.length, data.colors, colorMap);

  function buildDataSets(data: PieChartData): ChartData<'pie', unknown, unknown>['datasets'] {
    const backgroundColors = [];
    const borderColors = [];

    for (let i = 0; i < data.values.length; i++) {
      borderColors.push(chartColors[i].end);
      backgroundColors.push(chartColors[i].start);
    }

    return [
      {
        data: data.values,
        backgroundColor: backgroundColors,
        borderColor: borderColors
      }
    ];
  }

  // ChartJS Options
  const chartOptions: ChartOptions<'pie'> = {
    animation: defaultChartAnimation,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    }
  };

  // ChartJS Data
  const chartData: ChartData<'pie', unknown, unknown> = {
    labels: data.labels,
    datasets: buildDataSets(data)
  };

  return (
    <div>
      <Chart id="chart">
        {loading ? (
          <SkeletonWrapper>
            <Skeleton variant="rectangular" width="100%" />
          </SkeletonWrapper>
        ) : (
          <Pie data={chartData} options={chartOptions} ref={pieRef} />
        )}
      </Chart>
      {!disableLegend && (
        <ChartLegend
          data={data}
          colors={chartColors}
          loading={loading}
          marker="square"
          skeletonOrientation="horizontal"
          mouseOverCallback={(i: any) => legendMouseOver(pieRef, i, true)}
          mouseLeaveCallback={() => legendMouseLeave(pieRef)}
        />
      )}
    </div>
  );
};

export default forwardRef(PieChart);

const Chart = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  max-width: ${chartMaxHeight}px;
  height: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.values['md']}px) {
    max-width: ${chartMaxHeight}px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.values['lg']}px) {
    max-width: ${chartMaxHeight}px;
  }
`;

const SkeletonWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  .MuiSkeleton-root {
    padding-bottom: 100%;
    border-radius: 50%;
  }
`;
